// Arma el objeto `Rol` de SPEC.md §2 para un TORNEO INTERNO del club, desde los juegos
// de un dia (tabla `juegos`, escrita desde el punto de vista del club: grupo propio,
// rival, puntos a favor/en contra, condicion local|visita, cancha o campo_numero).
//
// Diferencias con el rol de la red (mapeoRol.js):
// - La "categoria" es el GRUPO del club: es lo que distingue un partido de otro.
// - Un lado siempre es el club (nombre y escudo de la organizacion); el otro, el rival
//   del catalogo de equipos. De visita, el rival va a la izquierda (juegaDeVisita).
// - El EQUIPO del club es el GRUPO, no el club: U12 y U16 pueden jugar a la misma hora en
//   campos distintos y eso es legal (el dueno, 2026-09-08, al exportar LTL KIDS). Por eso
//   hay una entidad por grupo, todas con la misma cara (nombre y escudo del club).
// - El rival se llama como su CLUB ("DINOS", "DOMUS"), no como su equipo, asi que tambien
//   puede traer dos equipos a la misma hora: va marcado `varios_equipos` para que la
//   validacion no lo lea como choque.
// - La jornada es opcional: si todos los juegos del dia traen la misma, se nombra.
// - No hay descansos: no existe la nocion de inscripcion.
// - El numero viene de juegos.numero (20260907c): "J3 · #12" o "#12" sin jornada.

import { FLYER_PALETTE } from '../lib/rolFlyer';
import { juegaDeVisita, campoDe } from '../lib/rolInterno';
import { fixMojibake } from '../lib/utils';
import { etiquetaCorta, aColorClaro } from './mapeoRol';

const hhmm = (h) => (h ? String(h).slice(0, 5) : null);
const limpio = (s) => (s ? fixMojibake(String(s)).trim() : '');

// La jornada del dia: solo si todos los juegos con dato traen la misma y es un numero.
export const jornadaDelDia = (juegos) => {
  const set = new Set((juegos || []).map((j) => (j?.jornada == null ? '' : String(j.jornada).trim())).filter(Boolean));
  if (set.size !== 1) return null;
  const unica = [...set][0];
  return /^\d+$/.test(unica) ? Number(unica) : null;
};

export function armarRolInterno({ torneo, org, fecha, juegos, generadoEn }) {
  const js = (Array.isArray(juegos) ? juegos : []).filter(Boolean);
  const jornada = jornadaDelDia(js);

  // Campos: cancha o "Campo N" de cada juego, en orden natural; "Sin cancha" al final.
  const nombresCampo = Array.from(new Set(js.map((j) => campoDe(j) || 'Sin cancha')))
    .sort((a, b) => (a === 'Sin cancha' ? 1 : b === 'Sin cancha' ? -1 : a.localeCompare(b, 'es', { numeric: true })));
  const campoId = (n) => `c${nombresCampo.indexOf(n) + 1}`;
  const campos = nombresCampo.map((n) => ({ id: campoId(n), nombre: n, superficie: '' }));

  const horarios = Array.from(new Set(js.map((j) => hhmm(j.hora)).filter(Boolean))).sort();

  // Categorias = grupos del club, color estable por orden alfabetico (como el flyer).
  const grupoDe = (j) => limpio(j.grupo?.nombre_del_grupo || j.grupos?.nombre_del_grupo) || 'Sin grupo';
  const nombresCat = Array.from(new Set(js.map(grupoDe))).sort();
  const categorias = nombresCat.map((nombre, i) => {
    const oscuro = FLYER_PALETTE[i % FLYER_PALETTE.length];
    return { id: `g-${i + 1}`, nombre, etiqueta_corta: etiquetaCorta(nombre), color_oscuro: oscuro, color_claro: aColorClaro(oscuro) };
  });
  const catPorNombre = Object.fromEntries(categorias.map((c) => [c.nombre, c.id]));

  const clubes = {};
  const nombreClub = limpio(org?.name) || 'Mi club';
  // Una entidad por grupo: misma cara, distinta identidad para la validacion.
  const clubDelGrupo = (j) => {
    const grupo = grupoDe(j);
    const id = `club-${catPorNombre[grupo] || 'sin-grupo'}`;
    if (!clubes[id]) {
      clubes[id] = {
        id,
        nombre: nombreClub,
        nombre_corto: limpio(org?.short_name) || undefined,
        escudo_url: org?.logo_url || undefined,
        // Solo para los avisos: dice de que grupo se trata.
        etiqueta: `${nombreClub} · ${grupo}`,
      };
    }
    return id;
  };
  const rivalId = (j) => {
    const nombre = limpio(j.equipo?.nombre || j.equipo_nombre) || 'Por definir';
    const id = j.equipo?.id ? `eq-${j.equipo.id}` : `eq-${nombre.toLowerCase()}`;
    if (!clubes[id]) {
      clubes[id] = {
        id, nombre,
        nombre_corto: limpio(j.equipo?.nombre_corto) || undefined,
        escudo_url: j.equipo?.logo_url || undefined,
        varios_equipos: true,
      };
    }
    return id;
  };

  const partidos = js
    .filter((j) => hhmm(j.hora))
    .map((j) => {
      const rival = rivalId(j);
      const propio = clubDelGrupo(j);
      const [local, visitante] = juegaDeVisita(j) ? [rival, propio] : [propio, rival];
      const numero = j.numero ?? null;
      const id = numero == null ? '' : (jornada ? `J${jornada} · #${numero}` : `#${numero}`);
      return {
        id, numero, juego_id: j.id,
        campo_id: campoId(campoDe(j) || 'Sin cancha'),
        horario: hhmm(j.hora),
        categoria_id: catPorNombre[grupoDe(j)],
        local_id: local,
        visitante_id: visitante,
      };
    });

  const rol = {
    liga: limpio(torneo?.ligas?.nombre) || limpio(torneo?.organizador) || limpio(org?.name) || '',
    torneo: limpio(torneo?.nombre),
    logo_url: torneo?.logo_url || torneo?.ligas?.logo_url || null,
    jornada,
    fecha: fecha || js.find((j) => j.fecha)?.fecha || null,
    sede: limpio(js.find((j) => j.ubicacion)?.ubicacion) || limpio(torneo?.sede) || '',
    version: null,
    generado_en: generadoEn || new Date().toISOString(),
    tolerancia_min: null,
    campos, horarios, categorias, partidos, descansos: [],
  };
  return { rol, clubes };
}
